/**
 * NE3: DIE AUFTRAGSKARTE (.579).
 *
 * ORIGINALTEXT MARVIN (02.11.2026)
 *   „Ich möchte gerne für NE3 auch eine Auftragsübersichtskarte, so wie bei
 *    NE4 haben, um dort mal einen Überblick zu haben. Bitte richte die
 *    entsprechend ein, auch mit den notwendigen Feldern, Auswahlen und Farben,
 *    wo ich dann oben über den Status wählen kann."
 *
 * Bauart wie die NE4-Karte (Kacheln, Punkte als SVG, stufenloses Zoomen,
 * Finger, Zahl bei überlappenden Punkten). Gefärbt wird nach dem STATUS der
 * Auftragsliste; voll heißt Tiefbau erledigt (oder kein Tiefbau nötig), hohl:
 * Tiefbau steht aus oder ist noch nicht vergeben. Oben
 * der Status als Chips, darunter „Was ansteht" wie in der Auftragsliste
 * (Wartegrund fehlt, Abrechenbar, Abgerechnet, Aufmaß prüfen, Doku prüfen,
 * Termin heute, Wartet, Einblasstopp), Gewerk, Tiefbaufirma und Suche. Ein Klick
 * öffnet die Akte.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type React from 'react';
import { api, datum, ZUSTAND_TEXT, type Anmelder } from '../api.ts';
import { ANSTEHT, GEWERKWAHL } from './Arbeitsliste.tsx';
import { Kopf, Karte } from '../teile/Geruest.tsx';
import { Fehlerkasten, ausFehler, type Fehlerlage } from '../teile/Formular.tsx';
import { useFrisch } from '../teile/Frisch.tsx';
import { ausPixel, nachPixel, kartenHoehe } from '../teile/kartenebene.ts';
import { PunktMenue, RoutenEbene, RoutenKnoepfe, RoutenLeiste, haltAus } from '../teile/Fahrtroute.tsx';

/* .579: DIESELBEN ZUSTÄNDE, NAMEN UND FARBEN WIE IN DER AUFTRAGSLISTE (z-neu,
   z-laufend, ... aus stil.css). Marvin: „die Kategorien müssen 1:1 angezeigt
   werden, wie wir sie auch sonst haben". */
export const STATUS: { wert: string; name: string; farbe: string }[] =
  ['neu', 'laufend', 'ueberfaellig', 'unterbrochen', 'erledigt'].map((w) => ({ wert: w, name: ZUSTAND_TEXT[w] ?? w, farbe: `var(--${w})` }));
export const BAUSTAND: Record<string, string> = {
  wartet_auf_tiefbau: 'Wartet auf Tiefbau',
  tiefbau_terminiert: 'Tiefbau terminiert',
  sofort_spleissbar: 'Sofort spleißbar',
  terminiert: 'Spleißen terminiert',
  spleisstermin_ueberfaellig: 'Spleißtermin überfällig',
  wartet: 'Wartet (Freischaltung)',
  unterbrochen: 'Unterbrochen',
  abgeschlossen: 'Abgeschlossen',
};
const farbeVon = (z: string) => STATUS.find((s) => s.wert === z)?.farbe ?? 'var(--unterbrochen)';
const MAXZ = 20; const MAXZ_KACHEL = 19;
const MERK = 'mds.ne3karte.aus';
type P = { lat: number; lon: number };

function passendeStufe(punkte: P[], breite: number, hoehe: number): { mitte: P; zoom: number } {
  if (!punkte.length) return { mitte: { lat: 50.36, lon: 7.59 }, zoom: 11 };
  const lats = punkte.map((p) => p.lat), lons = punkte.map((p) => p.lon);
  const mitte = { lat: (Math.min(...lats) + Math.max(...lats)) / 2, lon: (Math.min(...lons) + Math.max(...lons)) / 2 };
  for (let z = 16; z >= 8; z--) {
    const a = nachPixel({ lat: Math.max(...lats), lng: Math.min(...lons) }, z);
    const b = nachPixel({ lat: Math.min(...lats), lng: Math.max(...lons) }, z);
    if (Math.abs(b.x - a.x) < breite - 24 && Math.abs(b.y - a.y) < hoehe - 24) return { mitte, zoom: z };
  }
  return { mitte, zoom: 8 };
}

export function Ne3Karte({ oeffne }: { ich: Anmelder; oeffne: (id: string) => void }) {
  const [d, setD] = useState<any>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [sicht, setSicht] = useState<{ cx: number; cy: number; zoom: number } | null>(null);
  const [breite, setBreite] = useState(900);
  const [hoehe, setHoehe] = useState(560);
  /* GEMERKT WIRD, WAS AUS IST; erledigt ist beim ersten Öffnen aus. */
  const [aus, setAus] = useState<Set<string>>(() => {
    try { const s = localStorage.getItem(MERK); if (s) return new Set(JSON.parse(s)); } catch { /* ohne Speicher */ }
    return new Set(['erledigt']);
  });
  useEffect(() => { try { localStorage.setItem(MERK, JSON.stringify([...aus])); } catch { /* egal */ } }, [aus]);
  const [ansteht, setAnsteht] = useState('');
  const [gewerk, setGewerk] = useState('');
  const [firma, setFirma] = useState('');
  const [suche, setSuche] = useState('');
  const [auswahl, setAuswahl] = useState<any[] | null>(null);
  /* .586: ein Klick auf einen Punkt öffnet erst das kleine Fenster (Auftrag öffnen, Route: Start, Route: Stopp). */
  const [menue, setMenue] = useState<{ x: number; y: number; p: any } | null>(null);
  const rahmen = useRef<HTMLDivElement>(null);
  const zieh = useRef<{ x: number; y: number; cx: number; cy: number; bewegt: boolean } | null>(null);
  const finger = useRef<{ x: number; y: number; abstand: number; cx: number; cy: number; zoom: number } | null>(null);
  const abstand = (t: React.TouchList) => t.length < 2 ? 0 : Math.hypot(t[0]!.clientX - t[1]!.clientX, t[0]!.clientY - t[1]!.clientY);
  const zoom = sicht?.zoom ?? 11;

  const laden = () => api.ne3Karte().then(setD).catch((f) => setFehler(ausFehler(f)));
  useEffect(() => { laden(); }, []);
  useFrisch(laden);
  useEffect(() => {
    const messen = () => {
      const el = rahmen.current; if (!el) return;
      setBreite(Math.max(300, Math.floor(el.clientWidth) - 2));
      setHoehe(kartenHoehe(el));
    };
    messen(); window.addEventListener('resize', messen);
    const ro = typeof ResizeObserver !== 'undefined' && rahmen.current ? new ResizeObserver(() => messen()) : null;
    if (ro && rahmen.current) ro.observe(rahmen.current);
    return () => { window.removeEventListener('resize', messen); ro?.disconnect(); };
  }, [!!d]);
  useEffect(() => {
    const el = rahmen.current; if (!el) return;
    const halt = (e: WheelEvent) => { if ((e.target as HTMLElement)?.closest?.('.ne4-karte-rahmen')) e.preventDefault(); };
    el.addEventListener('wheel', halt, { passive: false });
    return () => el.removeEventListener('wheel', halt);
  }, [!!d]);

  const alle: any[] = d?.punkte ?? [];
  const firmen = useMemo(() => [...new Set(alle.map((p) => p.tiefbau).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'de')), [d]);
  const s = suche.trim().toLowerCase();
  const passtOhneStatus = (p: any) => (!ansteht || !!p[ansteht]) && (!gewerk || (p.mit_termin ?? []).includes(gewerk))
    && (!firma || (firma === '-' ? !p.tiefbau : p.tiefbau === firma))
    && (!s || [p.strasse, p.hausnummer, p.plz, p.ort, p.kls_id, p.sm_nummer].some((x) => String(x ?? '').toLowerCase().includes(s)));
  const punkte = alle.filter((p) => !aus.has(p.zustand) && passtOhneStatus(p));
  const zahl = (z: string) => alle.filter((p) => p.zustand === z && passtOhneStatus(p)).length;
  const umschalten = (z: string) => { setAus((a) => { const n = new Set(a); if (n.has(z)) n.delete(z); else n.add(z); return n; }); setSicht(null); };

  const setzeMitte = (p: P, z: number) => { const q = nachPixel({ lat: p.lat, lng: p.lon }, z); setSicht({ cx: q.x, cy: q.y, zoom: z }); };
  /* Neu einpassen, wenn sich die Auswahl ändert und niemand verschoben hat. */
  useEffect(() => {
    if (!d || sicht) return;
    const q = passendeStufe((punkte.length ? punkte : alle).map((x: any) => ({ lat: Number(x.lat), lon: Number(x.lon) })), breite, hoehe);
    setzeMitte(q.mitte, q.zoom);
  }, [d, breite, sicht, aus, ansteht, gewerk, firma]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setMenue(null); }, [sicht]); // .586: nach Schieben oder Zoomen steht das Fenster nicht mehr am Punkt
  const lage = (p: P) => {
    if (!sicht) return { x: 0, y: 0 };
    const q = nachPixel({ lat: p.lat, lng: p.lon }, sicht.zoom);
    return { x: q.x - sicht.cx + breite / 2, y: q.y - sicht.cy + hoehe / 2 };
  };
  const ausPixelLage = (px: number, py: number): P => { if (!sicht) return { lat: 50.3, lon: 7.6 }; const g = ausPixel(sicht.cx + px - breite / 2, sicht.cy + py - hoehe / 2, sicht.zoom); return { lat: g.lat, lon: g.lng }; };
  const zoomenUm = (n: number, px?: number, py?: number) => setSicht((v) => {
    if (!v) return v;
    const z2 = Math.max(8, Math.min(MAXZ, v.zoom + n)); if (z2 === v.zoom) return v;
    const f = 2 ** (z2 - v.zoom); const ox = (px ?? breite / 2) - breite / 2, oy = (py ?? hoehe / 2) - hoehe / 2;
    return { zoom: z2, cx: (v.cx + ox) * f - ox, cy: (v.cy + oy) * f - oy };
  });
  const zoomenStufe = (n: number) => setSicht((v) => {
    if (!v) return v;
    const ziel = n > 0 ? Math.floor(v.zoom + 1e-6) + 1 : Math.ceil(v.zoom - 1e-6) - 1;
    const z2 = Math.max(8, Math.min(MAXZ, ziel)); if (z2 === v.zoom) return v;
    const f = 2 ** (z2 - v.zoom); return { zoom: z2, cx: v.cx * f, cy: v.cy * f };
  });
  const schieben = (dx: number, dy: number) => setSicht((v) => v ? { ...v, cx: v.cx + dx, cy: v.cy + dy } : v);
  const maus = {
    onMouseDown: (e: React.MouseEvent) => { if (!sicht || e.button !== 0) return; zieh.current = { x: e.clientX, y: e.clientY, cx: sicht.cx, cy: sicht.cy, bewegt: false }; },
    onMouseMove: (e: React.MouseEvent) => {
      const z = zieh.current; if (!z || !(e.buttons & 1)) return;
      const dx = z.x - e.clientX, dy = z.y - e.clientY;
      if (!z.bewegt && Math.abs(dx) + Math.abs(dy) < 4) return;
      z.bewegt = true; setSicht((v) => v ? { ...v, cx: z.cx + dx, cy: z.cy + dy } : v);
    },
    onMouseUp: () => { setTimeout(() => { zieh.current = null; }, 0); },
    onMouseLeave: () => { zieh.current = null; },
    onWheel: (e: React.WheelEvent) => {
      const r = rahmen.current?.getBoundingClientRect(); if (!r) return;
      const roh = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
      const schritt = Math.max(-0.5, Math.min(0.5, -roh / 200));
      if (schritt) zoomenUm(schritt, e.clientX - r.left, e.clientY - r.top);
    },
  };
  const beruehrung = {
    onTouchStart: (e: React.TouchEvent) => {
      if (!sicht) return; const t = e.touches;
      const x = t.length >= 2 ? (t[0]!.clientX + t[1]!.clientX) / 2 : (t[0]?.clientX ?? 0);
      const y = t.length >= 2 ? (t[0]!.clientY + t[1]!.clientY) / 2 : (t[0]?.clientY ?? 0);
      finger.current = { x, y, abstand: abstand(t), cx: sicht.cx, cy: sicht.cy, zoom: sicht.zoom };
    },
    onTouchMove: (e: React.TouchEvent) => {
      const f = finger.current; if (!f) return;
      if (e.cancelable) e.preventDefault();
      const t = e.touches;
      if (t.length >= 2 && f.abstand > 0) {
        const q = abstand(t) / f.abstand; const r = rahmen.current?.getBoundingClientRect();
        const mx = (t[0]!.clientX + t[1]!.clientX) / 2 - (r?.left ?? 0), my = (t[0]!.clientY + t[1]!.clientY) / 2 - (r?.top ?? 0);
        const z2 = Math.max(8, Math.min(MAXZ, f.zoom + Math.log2(q))); const fak = 2 ** (z2 - f.zoom);
        const ox = mx - breite / 2, oy = my - hoehe / 2;
        setSicht({ zoom: z2, cx: (f.cx + ox) * fak - ox, cy: (f.cy + oy) * fak - oy }); return;
      }
      const dx = f.x - (t[0]?.clientX ?? f.x), dy = f.y - (t[0]?.clientY ?? f.y);
      setSicht((v) => v ? { ...v, zoom: f.zoom, cx: f.cx + dx, cy: f.cy + dy } : v);
    },
    onTouchEnd: (e: React.TouchEvent) => {
      if (e.touches.length === 0) finger.current = null;
      else if (sicht) { const t = e.touches; finger.current = { x: t[0]!.clientX, y: t[0]!.clientY, abstand: 0, cx: sicht.cx, cy: sicht.cy, zoom: sicht.zoom }; }
    },
  };
  const kacheln = useMemo(() => {
    if (!sicht) return [] as { key: string; src: string; x: number; y: number; g: number }[];
    const zt = Math.max(8, Math.min(MAXZ_KACHEL, Math.round(sicht.zoom)));
    const sk = 2 ** (sicht.zoom - zt); const g = 256 * sk; const n = 2 ** zt;
    const cxT = sicht.cx / sk, cyT = sicht.cy / sk;
    const x0 = Math.floor((cxT - breite / 2 / sk) / 256), x1 = Math.floor((cxT + breite / 2 / sk) / 256);
    const y0 = Math.floor((cyT - hoehe / 2 / sk) / 256), y1 = Math.floor((cyT + hoehe / 2 / sk) / 256);
    const l: { key: string; src: string; x: number; y: number; g: number }[] = [];
    for (let tx = x0; tx <= x1; tx++) for (let ty = y0; ty <= y1; ty++) {
      if (ty < 0 || ty >= n) continue;
      const wx = ((tx % n) + n) % n;
      l.push({ key: `${zt}/${tx}/${ty}`, src: `/api/karte/kachel/${zt}/${wx}/${ty}`, x: (tx * 256 - cxT) * sk + breite / 2, y: (ty * 256 - cyT) * sk + hoehe / 2, g });
    }
    return l;
  }, [sicht, breite, hoehe]);

  const titelVon = (p: any) => [
    `${p.strasse ?? ''} ${p.hausnummer ?? ''}, ${p.plz ?? ''} ${p.ort ?? ''}`,
    `KLS ${p.kls_id ?? '-'}${p.sm_nummer ? `, SM ${p.sm_nummer}` : ''}`,
    `${STATUS.find((x) => x.wert === p.zustand)?.name ?? p.zustand}${p.zustand === 'ueberfaellig' && p.ueberfaellig_art ? ` (${p.ueberfaellig_art})` : ''}, ${BAUSTAND[p.stand] ?? p.stand ?? ''}`,
    p.tb_termin ? `Tiefbau ${datum(p.tb_termin)}` : '', p.sp_termin ? `Spleißen ${datum(p.sp_termin)}` : '',
    p.tiefbau ? `Tiefbau: ${p.tiefbau}` : '', p.montage ? `Montage: ${p.montage}` : '',
    ANSTEHT.filter((k) => p[k.wert]).map((k) => k.name).join(', '),
  ].filter(Boolean).join('\n');

  return (
    <>
      <Kopf titel="Karte" weg={d ? `${punkte.length} von ${alle.length} Aufträgen gezeigt` : ''}
            rechts={<div className="reihe">
              <button type="button" className="knopf klein" onClick={() => zoomenStufe(1)} aria-label="Näher">+</button>
              <button type="button" className="knopf klein" onClick={() => zoomenStufe(-1)} aria-label="Weiter weg">−</button>
            </div>} />
      <div className="inhalt">
        <Fehlerkasten lage={fehler} />
        {d && !alle.length && <Karte><div className="leise">Noch kein Auftrag mit Koordinaten.</div></Karte>}
        <div className="ne4-karte" style={{ padding: '0 10px 0 2px' }}>
          <div className="ne4-karte-legende ne3-karte-status">
            <span className="klein leise ne4-karte-zeichen">Status:</span>
            {STATUS.map((st) => (
              <button key={st.wert} type="button" className={'knopf filter ne3-status' + (aus.has(st.wert) ? ' aus' : ' gewaehlt')}
                      aria-pressed={!aus.has(st.wert)} onClick={() => umschalten(st.wert)}>
                <span className="ne4-farbpunkt" style={{ background: st.farbe }} />
                {st.name}
                <span className="filterzahl">{zahl(st.wert)}</span>
              </button>
            ))}
            <button type="button" className="knopf klein" disabled={!aus.size} onClick={() => { setAus(new Set()); setSicht(null); }}>Alle an</button>
            <button type="button" className="knopf klein" disabled={aus.size === STATUS.length} onClick={() => { setAus(new Set(STATUS.map((x) => x.wert))); setSicht(null); }}>Alle aus</button>
          </div>
          {/* WAS ANSTEHT: DIE ZWEITE REIHE DER AUFTRAGSLISTE, dieselben Namen, Farben und Sätze (ANSTEHT aus Arbeitsliste.tsx). */}
          {ANSTEHT.some((k) => alle.some((p) => p[k.wert])) && (
            <div className="reihe ansteht ne3-karte-ansteht">
              <span className="klein leise anstehtkopf">Was ansteht</span>
              {ANSTEHT.filter((k) => alle.some((p) => p[k.wert]) || ansteht === k.wert).map((k) => (
                <button key={k.wert} type="button" title={k.satz}
                        className={'knopf filter ' + (k.farbe || '') + (ansteht === k.wert ? ' gewaehlt' : '')}
                        aria-pressed={ansteht === k.wert}
                        onClick={() => { setAnsteht(ansteht === k.wert ? '' : k.wert); setSicht(null); }}>
                  {k.name}<span className="filterzahl">{alle.filter((p) => p[k.wert] && !aus.has(p.zustand)).length}</span>
                </button>
              ))}
            </div>
          )}
          <div className="ne3-karte-filter">
            <label className="feld"><span>Gewerk</span>
              <select value={gewerk} onChange={(e) => { setGewerk(e.target.value); setSicht(null); }}>
                <option value="">Alle Gewerke</option>
                {GEWERKWAHL.map((g) => <option key={g.wert} value={g.wert}>{g.text}</option>)}
              </select>
            </label>
            <label className="feld"><span>Tiefbaufirma</span>
              <select value={firma} onChange={(e) => { setFirma(e.target.value); setSicht(null); }}>
                <option value="">alle</option>
                <option value="-">noch keine</option>
                {firmen.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </label>
            <label className="feld ne3-karte-suche"><span>Suche</span>
              <input value={suche} placeholder="Straße, Ort, KLS-ID, SM-Nummer" onChange={(e) => setSuche(e.target.value)}
                     onKeyDown={(e) => { if (e.key === 'Enter') setSicht(null); }} />
            </label>
            <span className="klein leise ne3-karte-zeichen">
              <span className="ne4-farbpunkt" style={{ background: '#444' }} /> Tiefbau erledigt, <span className="ne4-farbpunkt hohl" /> Tiefbau steht aus
            </span>
          </div>
          <RoutenLeiste oeffnen={(h) => oeffne(h.id)} />
          <div className="ne4-karte-flaeche" ref={rahmen}>
            <div className="ne4-karte-rahmen" style={{ height: hoehe }} {...maus} {...beruehrung}>
              {kacheln.map((k) => (
                <img key={k.key} src={k.src} alt="" width={256} height={256} draggable={false}
                     style={{ left: k.x, top: k.y, width: k.g + 0.5, height: k.g + 0.5 }} onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = 'hidden'; }} />
              ))}
              <svg width={breite} height={hoehe} viewBox={`0 0 ${breite} ${hoehe}`}>
                {(() => {
                  const gruppen: { x: number; y: number; p: any[] }[] = [];
                  for (const p of punkte) {
                    const { x, y } = lage({ lat: Number(p.lat), lon: Number(p.lon) });
                    if (x < -20 || y < -20 || x > breite + 20 || y > hoehe + 20) continue;
                    const g = gruppen.find((q) => Math.hypot(q.x - x, q.y - y) < 16);
                    if (g) { g.p.push(p); g.x = (g.x * (g.p.length - 1) + x) / g.p.length; g.y = (g.y * (g.p.length - 1) + y) / g.p.length; }
                    else gruppen.push({ x, y, p: [p] });
                  }
                  return gruppen.map((g) => {
                    if (g.p.length > 1) {
                      /* Die Farbe der Gruppe ist die des dringlichsten Status darin. */
                      const rang = ['ueberfaellig', 'unterbrochen', 'neu', 'laufend', 'erledigt'];
                      const z = rang.find((r) => g.p.some((p: any) => p.zustand === r)) ?? g.p[0].zustand;
                      return (
                        <g key={'g' + g.p[0].id} className="ne4-punkt" style={{ cursor: 'pointer' }}
                           onClick={() => { if (zieh.current?.bewegt) return; if (zoom < MAXZ_KACHEL) setzeMitte(ausPixelLage(g.x, g.y), Math.min(MAXZ_KACHEL, zoom + 4)); else setAuswahl(g.p); }}>
                          <title>{`${g.p.length} Aufträge:\n` + g.p.map((p: any) => `${p.strasse ?? ''} ${p.hausnummer ?? ''} (${p.kls_id ?? ''})`).join('\n')}</title>
                          <circle cx={g.x} cy={g.y} r={12} style={{ fill: farbeVon(z) }} stroke="#fff" strokeWidth={2.5} />
                          <text x={g.x} y={g.y + 4} fontSize={11} fontWeight={700} fill="#fff" textAnchor="middle">{g.p.length}</text>
                        </g>
                      );
                    }
                    const p = g.p[0]; const f = farbeVon(p.zustand);
                    return (
                      <g key={p.id} className="ne4-punkt" style={{ cursor: 'pointer' }} onClick={() => { if (!zieh.current?.bewegt) setMenue({ x: g.x, y: g.y, p }); }}>
                        <title>{titelVon(p)}</title>
                        <circle cx={g.x} cy={g.y} r={6} style={{ fill: p.tiefbau_erledigt ? f : '#fff', stroke: f }} strokeWidth={2.5} />
                      </g>
                    );
                  });
                })()}
                <RoutenEbene lage={(o) => lage({ lat: o.lat, lon: o.lon })} />
              </svg>
              {menue && <PunktMenue x={menue.x} y={menue.y} breite={breite} titel={`${menue.p.strasse ?? ''} ${menue.p.hausnummer ?? ''}, ${menue.p.kls_id ?? ''}`} h={haltAus('ne3', menue.p)}
                                    oeffnen={() => { const id = menue.p.id; setMenue(null); oeffne(id); }} zu={() => setMenue(null)} />}
              {auswahl && (
                <div className="ne4-karte-auswahl">
                  <div className="reihe" style={{ justifyContent: 'space-between', alignItems: 'center' }}><b>{auswahl.length} Aufträge an diesem Punkt</b><button type="button" className="knopf klein" onClick={() => setAuswahl(null)}>schließen</button></div>
                  {auswahl.map((p: any) => (
                    <div key={p.id} className="routen-gruppenzeile">
                      <button type="button" className="ne4-gebiet-zeile" onClick={() => { setAuswahl(null); oeffne(p.id); }}>
                        <span className="ne4-farbpunkt" style={{ background: farbeVon(p.zustand) }} />
                        <span className="wachsen">{p.strasse} {p.hausnummer}<span className="klein leise">, {p.kls_id}, {BAUSTAND[p.stand] ?? p.stand}</span></span>
                      </button>
                      <RoutenKnoepfe h={haltAus('ne3', p)} />
                    </div>
                  ))}
                </div>
              )}
              <div className="ne4-karte-pfeile">
                <button type="button" className="knopf klein" onClick={() => schieben(0, -hoehe / 3)} aria-label="Nach Norden">▲</button>
                <div><button type="button" className="knopf klein" onClick={() => schieben(-breite / 3, 0)} aria-label="Nach Westen">◀</button><button type="button" className="knopf klein" onClick={() => schieben(breite / 3, 0)} aria-label="Nach Osten">▶</button></div>
                <button type="button" className="knopf klein" onClick={() => schieben(0, hoehe / 3)} aria-label="Nach Süden">▼</button>
              </div>
            </div>
            <div className="klein leise" style={{ marginTop: 6 }}>
              Ziehen schiebt, das Mausrad zoomt um den Zeiger (Telefon: 2 Finger), ein Klick auf einen Punkt: Akte öffnen oder in die Route (Start, Stopp).
              {d?.ohne_punkt ? ` ${d.ohne_punkt} Aufträge haben noch keine Koordinaten und fehlen hier.` : ''}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
